import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * Desktop mega-menu custom element.
 *
 * Hover on a level-1 item → opens its submenu panel.
 * Hover on a level-2 item (if it has children) → opens level-3.
 * Focus (keyboard) mirrors pointer behaviour for full ADA compliance.
 *
 * @typedef {{ activeItem: HTMLElement | null, activeNestedItem: HTMLElement | null }} State
 * @typedef {{ overflowMenu: HTMLElement, submenu?: HTMLElement[] }} Refs
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ["overflowMenu"];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const signal = this.#abortController.signal;

    // Close when pointer leaves the whole overflow menu area
    this.overflowMenu?.addEventListener(
      "pointerleave",
      () => this.#debouncedDeactivate(),
      { signal },
    );

    // Keyboard: Escape closes the active submenu
    this.addEventListener("keydown", this.#onKeydown, { signal });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @type {State} */
  #state = { activeItem: null, activeNestedItem: null };

  /**
   * Milliseconds to allow for a closing animation before fully deactivating.
   * Set via data-animation-delay on the element.
   * @returns {number}
   */
  get animationDelay() {
    const parsedValue = parseInt(this.dataset.animationDelay ?? "0", 10);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  /** @returns {HTMLElement | null} */
  get overflowMenu() {
    const overflowRef = this.refs.overflowMenu;
    if (!(overflowRef instanceof HTMLElement)) return null;
    const shadowOverflow = overflowRef.shadowRoot?.querySelector('[part="overflow"]');
    return shadowOverflow instanceof HTMLElement ? shadowOverflow : overflowRef;
  }

  /** @returns {boolean} */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(":hover") ?? false;
  }

  // ─── Public handlers (wired from Liquid via event attributes) ────────────

  /**
   * Called on pointerenter / focusin of a menu item.
   * Cancels any pending deactivation and schedules activation.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedActivateHandler(event);
  };

  /**
   * Called on pointerleave / focusout of a menu item.
   * Schedules deactivation if the leaving item is still active.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;
    if (isMovingWithinItem(event, item)) return;
    if (item === this.#state.activeItem) this.#debouncedDeactivate();
  }

  /**
   * Handles level-2 items with children without changing level-1 active state.
   * @param {PointerEvent | FocusEvent} event
   */
  activateNested(event) {
    if (!(event.target instanceof Element)) return;
    const nestedItem = findNestedMenuItem(event.target);
    if (!nestedItem) return;

    if (this.#state.activeNestedItem && this.#state.activeNestedItem !== nestedItem) {
      this.#collapseNestedItem(this.#state.activeNestedItem);
    }

    this.#expandNestedItem(nestedItem);
    this.#state.activeNestedItem = nestedItem;
  }

  /**
   * Collapses nested level-3 panel when pointer/focus leaves the level-2 item.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivateNested(event) {
    if (!(event.target instanceof Element)) return;
    const nestedItem = findNestedMenuItem(event.target);
    if (!nestedItem) return;

    if (isMovingWithinItem(event, nestedItem)) return;

    this.#collapseNestedItem(nestedItem);
    if (this.#state.activeNestedItem === nestedItem) {
      this.#state.activeNestedItem = null;
    }
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute("data-animating");

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === "";
    this.dataset.overflowExpanded = String(!isDefaultSlot);

    // Collapse previously active item
    if (this.#state.activeItem) {
      this.#collapseItem(this.#state.activeItem);
    }

    if (this.#state.activeNestedItem) {
      this.#collapseNestedItem(this.#state.activeNestedItem);
      this.#state.activeNestedItem = null;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = "true";
    this.#expandItem(item);

    // Update submenu height CSS custom property for animated reveals
    let submenu = findSubmenu(item);
    const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) submenu = this.overflowMenu;

    const submenuHeight = submenu
      ? Math.max(submenu.offsetHeight, overflowMenuHeight)
      : 0;
    this.style.setProperty("--submenu-height", `${submenuHeight}px`);
    this.style.setProperty("--submenu-opacity", "1");
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty("--submenu-height", "0px");
    this.style.setProperty("--submenu-opacity", "0");
    this.dataset.overflowExpanded = "false";
    this.#state.activeItem = null;
    this.#state.activeNestedItem = null;
    this.ariaExpanded = "false";

    this.#collapseItem(item);
    item.setAttribute("data-animating", "");

    // Remove animating flag slightly before animation ends so header can transition
    setTimeout(
      () => item.removeAttribute("data-animating"),
      Math.max(0, this.animationDelay - 150),
    );
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Expand a menu item and its associated submenu panel.
   * Also updates aria-expanded on the trigger button inside the item.
   * @param {HTMLElement} item
   */
  #expandItem(item) {
    item.ariaExpanded = "true";
    const submenu = findSubmenu(item);
    if (submenu) {
      submenu.hidden = false;
      submenu.removeAttribute("hidden");
    }
    // Sync the trigger inside the item (<a> or <button>)
    const trigger = item.querySelector('[aria-expanded]');
    if (trigger) trigger.ariaExpanded = "true";
  }

  /**
   * Collapse a menu item and its associated submenu panel.
   * @param {HTMLElement} item
   */
  #collapseItem(item) {
    item.ariaExpanded = "false";
    const submenu = findSubmenu(item);
    if (submenu) submenu.hidden = true;
    const trigger = item.querySelector('[aria-expanded]');
    if (trigger) trigger.ariaExpanded = "false";
  }

  /**
   * @param {HTMLElement} item
   */
  #expandNestedItem(item) {
    if (!item.classList.contains("has-children")) return;
    item.ariaExpanded = "true";
    const trigger = item.querySelector('[aria-expanded]');
    if (trigger) trigger.ariaExpanded = "true";

    const nestedSubmenu = findNestedSubmenu(item);
    if (nestedSubmenu) {
      nestedSubmenu.hidden = false;
      nestedSubmenu.removeAttribute("hidden");
      requestAnimationFrame(() => {
        const submenuLeft = nestedSubmenu.querySelector('.submenu-left');
        const submenuInner = nestedSubmenu.closest('.header-menu__submenu-inner');

        if (!submenuLeft || !submenuInner) return;

        const requiredHeight = submenuLeft.offsetHeight + 50;
        const currentHeight = submenuInner.offsetHeight;

        if (requiredHeight > currentHeight) {
          submenuInner.style.setProperty('--min-height', `${requiredHeight}px`);
        }
      });
    }
  }

  /**
   * @param {HTMLElement} item
   */
  #collapseNestedItem(item) {
    item.ariaExpanded = "false";
    const trigger = item.querySelector('[aria-expanded]');
    if (trigger) trigger.ariaExpanded = "false";

    const nestedSubmenu = findNestedSubmenu(item);
    if (nestedSubmenu) nestedSubmenu.hidden = true;
  }

  /**
   * Keyboard handler — Escape closes active submenu and returns focus.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== "Escape") return;
    if (!this.#state.activeItem) return;

    const item = this.#state.activeItem;
    this.#debouncedDeactivate.cancel();
    this.#deactivate(item);

    // Return focus to the trigger
    const trigger = item.querySelector('[aria-expanded]');
    if (trigger instanceof HTMLElement) trigger.focus();
  };

  /** Remove lazy loading from images so they are ready when submenu opens. */
  #preloadImages = () => {
    this.querySelectorAll('img[loading="lazy"]').forEach((img) =>
      img.removeAttribute("loading"),
    );
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Walk up from `element` to find the closest `[ref="menuitem"]`.
 * Handles the special "More" overflow slot item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    return findMenuItem(
      element.parentElement?.querySelector('[slot="overflow"]'),
    );
  }

  return /** @type {HTMLElement | null} */ (
    element.closest('[ref="menuitem"], .header-menu__item, .menu-list__list-item')
  );
}

/**
 * Find the submenu panel associated with a menu item's parent `<li>`.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const hostItem =
    element instanceof Element
      ? element.closest('[ref="menuitem"], .header-menu__item, .menu-list__list-item')
      : null;
  const submenu = hostItem?.querySelector('[ref="submenu[]"], .header-menu__submenu, .menu-list__submenu');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findNestedMenuItem(element) {
  if (!(element instanceof Element)) return null;
  const item = element.closest(".header-menu__item.is-level-2.has-children");
  return item instanceof HTMLElement ? item : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findNestedSubmenu(element) {
  if (!(element instanceof Element)) return null;
  const nestedSubmenu = element.querySelector(".header-menu__list.is-level-3");
  return nestedSubmenu instanceof HTMLElement ? nestedSubmenu : null;
}

/**
 * @param {PointerEvent | FocusEvent} event
 * @param {HTMLElement} item
 * @returns {boolean}
 */
function isMovingWithinItem(event, item) {
  const maybeRelatedTarget = "relatedTarget" in event ? event.relatedTarget : null;
  return maybeRelatedTarget instanceof Node && item.contains(maybeRelatedTarget);
}


// =============================================================================
// MOBILE MENU
// Panel-push drawer matching the three-level design.
// Uses document-level delegation so it keeps working after section morph/re-render.
// =============================================================================

class MobileMenu {
  /** @type {string[]} */ #panelStack = [];
  /** @type {HTMLElement | null} */ #lastFocusedElement = null;

  /** Focusable selector for focus-trap */
  static #FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  constructor() {
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);
  }

  /** @returns {HTMLElement | null} */
  static #drawerEl() {
    return document.getElementById('mobile-menu');
  }

  /** @returns {HTMLElement | null} */
  static #overlayEl() {
    return document.getElementById('mobile-menu-overlay');
  }

  /** @returns {boolean} */
  #isOpen() {
    const drawer = MobileMenu.#drawerEl();
    return drawer instanceof HTMLElement && !drawer.hidden;
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

   if (event.target.closest('#mobile-menu-trigger')) {
      event.preventDefault();

      if (this.#isOpen()) {
        this.close();
        // document
        //   .querySelector('.mobile-menu .custom__search-input')
        //   ?.classList.remove('show');
      } else {
        this.open();

        // setTimeout(() => {
        //   document
        //     .querySelector('.mobile-menu .custom__search-input')
        //     ?.classList.add('show');
        // }, 0);
      }

      return;
    }

    if (event.target.closest('#mobile-menu-close')) {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.target.closest('#mobile-menu-overlay')) {
      event.preventDefault();
      this.close();
      return;
    }

    const drawer = MobileMenu.#drawerEl();
    if (!drawer || !event.target.closest('#mobile-menu')) return;

    const openBtn = event.target.closest('[data-mobile-open]');
    if (openBtn instanceof HTMLElement) {
      event.preventDefault();
      this.#pushPanel(openBtn.dataset.mobileOpen ?? '');
      return;
    }

    const backBtn = event.target.closest('.mobile-menu__back');
    if (backBtn) {
      event.preventDefault();
      this.#popPanel();
    }
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKeydown = (event) => {
    if (!this.#isOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      const drawer = MobileMenu.#drawerEl();
      if (drawer) this.#trapFocus(event, drawer);
    }
  };

  open() {
    const drawer = MobileMenu.#drawerEl();
    const overlay = MobileMenu.#overlayEl();
    const trigger = document.getElementById('mobile-menu-trigger');

    if (!drawer) return;

    const activeEl = document.activeElement;
    this.#lastFocusedElement = activeEl instanceof HTMLElement ? activeEl : null;

    drawer.hidden = false;
    if (overlay) {
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
    }
    trigger?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      const first = drawer.querySelector(MobileMenu.#FOCUSABLE);
      if (first instanceof HTMLElement) first.focus();
    });
  }

  close() {
    const drawer = MobileMenu.#drawerEl();
    const overlay = MobileMenu.#overlayEl();
    const trigger = document.getElementById('mobile-menu-trigger');

    if (drawer) drawer.hidden = true;
    if (overlay) {
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
    }
    trigger?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    this.#panelStack = [];
    if (drawer) this.#syncPanels('0');

    this.#lastFocusedElement?.focus({ preventScroll: true });
  }

  /**
   * Push a panel onto the stack and show it.
   * @param {string} panelId
   */
  #pushPanel(panelId) {
    const drawer = MobileMenu.#drawerEl();
    if (!drawer || panelId === '') return;

    this.#panelStack.push(panelId);
    this.#syncPanels(panelId);

    const trigger = drawer.querySelector(`[data-mobile-open="${CSS.escape(panelId)}"]`);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  /** Pop the top panel and show the one below it. */
  #popPanel() {
    if (this.#panelStack.length === 0) return;

    const drawer = MobileMenu.#drawerEl();
    const closing = this.#panelStack.pop();

    if (drawer && closing) {
      const trigger = drawer.querySelector(`[data-mobile-open="${CSS.escape(closing)}"]`);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    const previous = this.#panelStack[this.#panelStack.length - 1] ?? '0';
    this.#syncPanels(previous);
  }

  /**
   * Show only the active panel; hide all others.
   * Injects a Back button if we're not at the root.
   * @param {string} activePanelId
   */
  #syncPanels(activePanelId) {
    const drawer = MobileMenu.#drawerEl();
    if (!drawer) return;

    const allPanels = /** @type {NodeListOf<HTMLElement>} */ (
      drawer.querySelectorAll('[data-panel]')
    );

    allPanels.forEach((panel) => {
      const isActive = panel.dataset.panel === activePanelId;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    });

    const activePanel = drawer.querySelector(`[data-panel="${CSS.escape(activePanelId)}"]`);
    if (!activePanel) return;

    activePanel.querySelector('.mobile-menu__back')?.remove();

    if (this.#panelStack.length > 0) {
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'mobile-menu__back';
      back.setAttribute('aria-label', 'Go back');
      back.innerHTML = 'BACK';
      activePanel.prepend(back);
    }

    requestAnimationFrame(() => {
      const first = activePanel.querySelector(MobileMenu.#FOCUSABLE);
      if (first instanceof HTMLElement) first.focus();
    });
  }

  /**
   * Trap focus within the drawer.
   * @param {KeyboardEvent} event
   * @param {HTMLElement} drawer
   */
  #trapFocus(event, drawer) {
    const focusable = /** @type {HTMLElement[]} */ (
      Array.from(drawer.querySelectorAll(MobileMenu.#FOCUSABLE))
    ).filter((el) => !el.closest('[hidden]'));

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }
}

/** Window guard: script may be included more than once (section + block). */
function initMobileMenu() {
  const w = /** @type {Window & { __lcrCoinMobileMenuInit?: boolean }} */ (window);
  if (w.__lcrCoinMobileMenuInit) return;
  w.__lcrCoinMobileMenuInit = true;
  new MobileMenu();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileMenu, { once: true });
} else {
  initMobileMenu();
}